import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Question, Questionnaire } from '@app/core/models/questionnaire.model';
import { NPSColor, NPSRepresentation, QuestionType } from '@app/core/enums/questionnaire.enums';
import { Textarea } from '@app/shared/components/form-fields/textarea/textarea';

@Component({
  selector: 'app-nps',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, Textarea],
  templateUrl: './nps.html',
  styleUrl: './nps.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Nps),
      multi: true,
    },
  ],
})
export class Nps implements OnInit {
  @Input() questionnaire: Questionnaire | null = null;
  @Input() question: Question | null = null;
  @Input() questionNumber: number = 0;
  @Input() isInvalid: boolean = false;
  @Input() errorMessage: string = '';
  @Input() formControl?: FormControl;

  @Output() answerChange = new EventEmitter<any>();

  selectedScore: number | null = null;
  disabled = false;
  npsRepresentation = NPSRepresentation;
  npsColor = NPSColor;
  questionType = QuestionType;
  npsScale: number[] = Array.from({ length: 11 }, (_, i) => i); // 0-10 scale
  currentFollowUpQuestions: any[] = [];
  followUpResponseControls: Map<string, FormControl> = new Map();
  followUpSelectedOptions: Map<string, string[]> = new Map();

  toNumber(value: any): number {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  }
  representation: NPSRepresentation = NPSRepresentation.BOXES;
  colorScheme: NPSColor = NPSColor.GRADIENT;

  onChange = (value: number | null) => {};
  onTouched = () => {};

  ngOnInit() {
    if (this.question?.questionSpecificOptions) {
      const options = this.question.questionSpecificOptions;
      this.representation =
        (options.npsRepresentation as NPSRepresentation) || NPSRepresentation.BOXES;
      this.colorScheme = (options.npsColor as NPSColor) || NPSColor.GRADIENT;
    }

    if (this.formControl) {
      this.formControl.valueChanges.subscribe((value) => {
        this.selectedScore = value;
      });
    }
  }

  writeValue(value: number): void {
    this.selectedScore = value !== undefined ? value : null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onScoreSelect(score: number): void {
    if (this.disabled) return;

    this.selectedScore = score;
    this.onChange(score);
    this.onTouched();

    // Check for follow-up questions
    if (this.question?.followUpQuestionFlag && this.question?.followUpQuestions) {
      const category = this.getScoreCategoryForFollowUp(score);
      // Filter all follow-up questions matching the category
      this.currentFollowUpQuestions = this.question.followUpQuestions.filter(
        (fuq: any) => fuq.questionCondition === category
      );

      // Initialize form controls and selected options for each follow-up question
      this.followUpResponseControls.clear();
      this.followUpSelectedOptions.clear();

      this.currentFollowUpQuestions.forEach((fuq: any) => {
        const questionId = fuq.followUpQuestionId || fuq.questionId;
        const control = new FormControl('');
        // Subscribe to value changes for text input follow-up questions
        const isTextInput =
          fuq.followUpQuestionType === this.questionType.TEXT_INPUT ||
          fuq.questionType === this.questionType.TEXT_INPUT ||
          fuq.questionType === 'TEXT_INPUT' ||
          fuq.questionType === 'TEXT';
        if (isTextInput) {
          control.valueChanges.subscribe((value) => {
            this.onFollowUpTextChange(questionId, value || '');
          });
        }
        this.followUpResponseControls.set(questionId, control);
        this.followUpSelectedOptions.set(questionId, []);
      });
    } else {
      this.currentFollowUpQuestions = [];
      this.followUpResponseControls.clear();
      this.followUpSelectedOptions.clear();
    }

    if (this.question?.questionId) {
      const response: any = {
        questionId: this.question.questionId,
        score: score,
      };

      if (this.currentFollowUpQuestions.length > 0) {
        response.followUpResponses = [];
      }

      this.answerChange.emit(response);
    }

    if (this.formControl) {
      this.formControl.setValue(score);
      this.formControl.markAsTouched();
    }
  }

  getScoreCategoryForFollowUp(score: number): string {
    if (score <= 6) return 'DETRACTORS';
    if (score <= 8) return 'PASSIVES';
    return 'PROMOTERS';
  }

  getScoreClass(score: number): string {
    if (this.colorScheme === NPSColor.GRADIENT) {
      return `score-${score}`;
    }
    return this.getScoreCategory(score);
  }

  isScoreHighlighted(score: number): boolean {
    // Only highlight scores BEFORE the selected score (not the selected score itself)
    return this.selectedScore !== null && score < this.selectedScore;
  }

  private getScoreCategory(score: number): string {
    if (score <= 6) return 'detractor';
    if (score <= 8) return 'passive';
    return 'promoter';
  }
  getScaleArray(count: number): number[] {
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  // Follow-up question handlers
  onFollowUpTextChange(questionId: string, value: string): void {
    if (!this.question) return;

    const allFollowUpResponses = this.buildAllFollowUpResponses();
    const existingIndex = allFollowUpResponses.findIndex((r: any) => r.questionId === questionId);

    const response = {
      questionId: questionId,
      textResponse: value,
      options: [],
      score: null,
    };

    if (existingIndex > -1) {
      allFollowUpResponses[existingIndex] = response;
    } else {
      allFollowUpResponses.push(response);
    }

    this.answerChange.emit({
      questionId: this.question.questionId,
      score: this.selectedScore,
      followUpResponses: allFollowUpResponses,
    });
  }

  onFollowUpOptionSelect(questionId: string, optionId: string, isMultiple: boolean = true): void {
    if (!this.question) return;

    const selectedOptions = this.followUpSelectedOptions.get(questionId) || [];

    if (isMultiple) {
      const index = selectedOptions.indexOf(optionId);
      if (index > -1) {
        selectedOptions.splice(index, 1);
      } else {
        selectedOptions.push(optionId);
      }
    } else {
      selectedOptions.length = 0;
      selectedOptions.push(optionId);
    }

    this.followUpSelectedOptions.set(questionId, selectedOptions);

    const allFollowUpResponses = this.buildAllFollowUpResponses();
    const existingIndex = allFollowUpResponses.findIndex((r: any) => r.questionId === questionId);

    const response = {
      questionId: questionId,
      textResponse: null,
      options: selectedOptions.map((optId) => ({
        optionId: optId,
        optionSelected: 'true',
      })),
      score: null,
    };

    if (existingIndex > -1) {
      allFollowUpResponses[existingIndex] = response;
    } else {
      allFollowUpResponses.push(response);
    }

    this.answerChange.emit({
      questionId: this.question.questionId,
      score: this.selectedScore,
      followUpResponses: allFollowUpResponses,
    });
    console.log('the user value', {
      questionId: this.question.questionId,
      score: this.selectedScore,
      followUpResponses: allFollowUpResponses,
    });
  }

  isOptionSelected(questionId: string, optionId: string): boolean {
    const selectedOptions = this.followUpSelectedOptions.get(questionId) || [];
    return selectedOptions.includes(optionId);
  }

  getFollowUpQuestionOptions(followUpQuestion: any): any[] {
    if (!followUpQuestion) return [];
    return followUpQuestion.followUpQuestionOptions || followUpQuestion.questionOptions || [];
  }

  getFollowUpResponseControl(questionId: string): FormControl {
    let control = this.followUpResponseControls.get(questionId);
    if (!control) {
      control = new FormControl('');
      this.followUpResponseControls.set(questionId, control);
    }
    return control;
  }

  private buildAllFollowUpResponses(): any[] {
    const responses: any[] = [];

    this.currentFollowUpQuestions.forEach((fuq: any) => {
      console.log('fuq', fuq);
      const questionId = fuq.followUpQuestionId
      const control = this.followUpResponseControls.get(questionId);
      const selectedOptions = this.followUpSelectedOptions.get(questionId) || [];

      // Check if it's a text input question
      const isTextInput =
        fuq.followUpQuestionType === this.questionType.TEXT_INPUT ||
        fuq.questionType === this.questionType.TEXT_INPUT ||
        fuq.questionType === 'TEXT_INPUT' ||
        fuq.questionType === 'TEXT';

      if (isTextInput && control?.value) {
        responses.push({
          questionId: questionId,
          textResponse: control.value,
          options: [],
          score: null,
        });
      } else if (!isTextInput && selectedOptions.length > 0) {
        responses.push({
          questionId: questionId,
          textResponse: null,
          options: selectedOptions.map((optId) => ({
            optionId: optId,
            optionSelected: 'true',
          })),
          score: null,
        });
      }
    });

    return responses;
  }
}
